/* @refresh reset */
import React from 'react'
import { Outlet, createRoute, AnyRoute } from '@tanstack/react-router'

import { sanitizeSearchParams } from '@shared/lib/url-search-utils'
import {
  isSafeEsignReturnId,
  parseFingerprintingEsignReturnSearch,
} from '@shared/lib/e-sign-return-navigation'
import { FINGERPRINTING_ROUTES } from '../constants/routes'
import { isFingerprintingStaff } from '../types/constants'
import clientAuthUtils from '../lib/auth.utils'
import { useClientAuthStore } from '../stores'

export const FINGERPRINTING_DOCUMENT_TITLE = 'Frontier One'
export const FINGERPRINTING_DOCUMENT_DESCRIPTION = 'Frontier One'

function FingerprintingDocumentHead() {
  React.useLayoutEffect(() => {
    const previousTitle = document.title
    const meta = document.querySelector('meta[name="description"]')
    const previousDescription = meta?.getAttribute('content') ?? null

    document.title = FINGERPRINTING_DOCUMENT_TITLE
    if (meta) meta.setAttribute('content', FINGERPRINTING_DOCUMENT_DESCRIPTION)

    return () => {
      document.title = previousTitle
      if (meta && previousDescription !== null) {
        meta.setAttribute('content', previousDescription)
      }
    }
  }, [])
  return null
}

// Lazy-loaded fingerprinting route components (code-splitting)
const LazyClientLayout = React.lazy(() => import('../features/client/layout'))
const LazyClientESign = React.lazy(() => import('../features/client/e-sign'))
const LazyClientInvoicePage = React.lazy(() => import('../features/client/invoice'))
const LazyClientLoginPage = React.lazy(() => import('../features/client/auth/login'))
const LazyConfirmDobPage = React.lazy(() => import('../features/client/auth/confirm-dob'))
const LazyForgotPasswordPage = React.lazy(() => import('../features/client/auth/forgot-password'))
const LazyResetPasswordPage = React.lazy(() => import('../features/client/auth/reset-password'))
const LazyClientApplication = React.lazy(() => import('../features/client/application'))
const LazyChangePasswordPage = React.lazy(() => import('../features/client/change-password'))
const LazyClientProtectedLayout = React.lazy(() => import('../features/client/layout/protected'))
const LazyClientApplicationDetail = React.lazy(
  () => import('../features/client/application/detail')
)
const LazyChangePasswordVerifyPage = React.lazy(
  () => import('../features/client/change-password/verify')
)
const LazyClientApplicationDetailDocument = React.lazy(
  () => import('../features/client/application/detail/document')
)
const LazyClientApplicationDetailInstructions = React.lazy(
  () => import('../features/client/application/detail/instructions')
)
const LazyClientChangePasswordVerifyLayout = React.lazy(
  () => import('../features/client/change-password/verify/layout')
)

const LazyStaffLayout = React.lazy(() => import('../features/staff/layout'))
const LazyStaffApplication = React.lazy(() => import('../features/staff/application'))
const LazyStaffDocumentPage = React.lazy(() => import('../features/staff/document'))
const LazyStaffProtectedLayout = React.lazy(() => import('../features/staff/layout/protected'))
const LazyStaffApplicationDetail = React.lazy(() => import('../features/staff/application/detail'))
const LazyStaffManualApplicationCreate = React.lazy(
  () => import('../features/staff/application/create')
)
const LazyStaffApplicationDetailDocument = React.lazy(
  () => import('../features/staff/application/detail/document')
)
const LazyStaffApplicationDetailInternalDocuments = React.lazy(
  () => import('../features/staff/application/detail/internal-documents')
)
const LazyStaffApplicationTypeDetailPage = React.lazy(
  () => import('../features/staff/document/detail')
)
const LazyStaffMessageTemplatePage = React.lazy(() => import('../features/staff/message-template'))
const LazyStaffChangePasswordPage = React.lazy(() => import('../features/staff/change-password'))
const LazyStaffManagementPage = React.lazy(() => import('../features/staff/staff-management'))
const LazyStaffAuditLogPage = React.lazy(() => import('../features/staff/audit-log'))

export const createFingerprintingRoutes = <T extends AnyRoute>(rootRoute: T) => {
  // Base fingerprinting route
  const fingerprintingRoute = createRoute({
    path: 'fingerprinting',
    getParentRoute: () => rootRoute,
    component: () => (
      <>
        <FingerprintingDocumentHead />
        <Outlet />
      </>
    ),

    head: () => ({
      meta: [
        {
          name: 'description',
          content: FINGERPRINTING_DOCUMENT_DESCRIPTION,
        },
        {
          title: FINGERPRINTING_DOCUMENT_TITLE,
        },
      ],
    }),
  })

  // Login route (outside client/staff protected areas)
  const fingerprintingClientLoginRoute = createRoute({
    path: 'login',
    component: () => <LazyClientLoginPage />,
    getParentRoute: () => fingerprintingRoute,
  })

  const fingerprintingForgotPasswordRoute = createRoute({
    path: 'forgot-password',
    component: () => <LazyForgotPasswordPage />,
    getParentRoute: () => fingerprintingRoute,
  })

  const fingerprintingResetPasswordRoute = createRoute({
    path: 'reset-password',
    component: () => <LazyResetPasswordPage />,
    getParentRoute: () => fingerprintingRoute,
  })

  const fingerprintingConfirmDobRoute = createRoute({
    path: 'confirm-dob',
    component: () => <LazyConfirmDobPage />,
    getParentRoute: () => fingerprintingRoute,
  })

  // Client routes
  const fingerprintingClientRoute = createRoute({
    path: 'client',
    getParentRoute: () => fingerprintingRoute,
    component: () => (
      <LazyClientProtectedLayout>
        <LazyClientLayout />
      </LazyClientProtectedLayout>
    ),
    beforeLoad: ({ location, navigate }) => {
      const user = useClientAuthStore.getState().user
      const roles = user?.roles || []
      const requiresDobConfirmation = useClientAuthStore.getState().requiresDobConfirmation

      // Redirect staff (FingerprintStaff or Admin) to staff routes
      if (isFingerprintingStaff(roles)) {
        navigate({ to: FINGERPRINTING_ROUTES.STAFF.BASE, replace: true })
        return
      }

      // Redirect client to confirm DOB if required
      if (requiresDobConfirmation) {
        navigate({ to: FINGERPRINTING_ROUTES.CONFIRM_DOB, replace: true })
        return
      }

      // Redirect client to their base route
      if (location.pathname.replace(/\/$/, '') === FINGERPRINTING_ROUTES.CLIENT.BASE) {
        navigate({ to: FINGERPRINTING_ROUTES.CLIENT.APPLICATION, replace: true })
      }
    },
  })

  const fingerprintingClientApplicationRoute = createRoute({
    path: 'application',
    getParentRoute: () => fingerprintingClientRoute,
    component: () => <LazyClientApplication />,
  })

  const fingerprintingClientApplicationDetailRoute = createRoute({
    getParentRoute: () => fingerprintingClientRoute,
    path: 'application/$applicationId',
    component: () => <LazyClientApplicationDetail />,
  })

  const fingerprintingClientApplicationDetailDocumentRoute = createRoute({
    getParentRoute: () => fingerprintingClientRoute,
    path: 'application/$applicationId/documents',
    validateSearch: (search: Record<string, unknown>) => {
      const s = sanitizeSearchParams(search)
      const out: { fileId?: string; documentId?: string; focusComment?: string } = {}
      if (s.fileId && isSafeEsignReturnId(s.fileId)) {
        out.fileId = s.fileId.trim()
      }
      if (s.documentId && isSafeEsignReturnId(s.documentId)) {
        out.documentId = s.documentId.trim()
      }
      if (s.focusComment === 'true') {
        out.focusComment = 'true'
      }
      return out
    },
    component: () => <LazyClientApplicationDetailDocument />,
  })

  const fingerprintingClientApplicationDetailInstructionsRoute = createRoute({
    getParentRoute: () => fingerprintingClientRoute,
    path: 'application/$applicationId/instructions',
    component: () => <LazyClientApplicationDetailInstructions />,
  })

  const fingerprintingClientInvoiceRoute = createRoute({
    path: 'invoice',
    component: () => <LazyClientInvoicePage />,
    getParentRoute: () => fingerprintingClientRoute,
  })

  const fingerprintingClientESignRoute = createRoute({
    path: 'e-signature',
    validateSearch: (search: Record<string, unknown>) =>
      parseFingerprintingEsignReturnSearch(sanitizeSearchParams(search) as Record<string, unknown>),
    component: () => <LazyClientESign />,
    getParentRoute: () => fingerprintingClientRoute,
  })

  const fingerprintingClientChangePasswordRoute = createRoute({
    path: 'change-password',
    component: () => <LazyChangePasswordPage />,
    getParentRoute: () => fingerprintingClientRoute,
  })

  // Change password verify route (outside protected client area)
  const fingerprintingClientChangePasswordVerifyRoute = createRoute({
    getParentRoute: () => fingerprintingRoute,
    path: 'change-password/verify',
    component: () => (
      <LazyClientChangePasswordVerifyLayout>
        <LazyChangePasswordVerifyPage />
      </LazyClientChangePasswordVerifyLayout>
    ),
  })

  // Staff routes
  const fingerprintingStaffRoute = createRoute({
    path: 'staff',
    component: () => (
      <LazyStaffProtectedLayout>
        <LazyStaffLayout />
      </LazyStaffProtectedLayout>
    ),
    getParentRoute: () => fingerprintingRoute,
    beforeLoad: ({ location, navigate }) => {
      const user = clientAuthUtils.getUser()
      const roles = user?.roles ?? []

      // Redirect staff to their base route
      if (location.pathname.replace(/\/$/, '') === FINGERPRINTING_ROUTES.STAFF.BASE) {
        navigate({ to: FINGERPRINTING_ROUTES.STAFF.APPLICATION, replace: true })
        return
      }

      // Enforce staff role - redirect non-staff to client routes
      // Only redirect if we have user data with roles and they're definitely not staff
      // (Don't redirect if user is null or roles are empty - might be loading or incomplete data)
      if (user && roles.length > 0 && !isFingerprintingStaff(roles)) {
        navigate({ to: FINGERPRINTING_ROUTES.CLIENT.BASE, replace: true })
      }
    },
  })

  const fingerprintingStaffApplicationRoute = createRoute({
    path: 'application',
    component: () => <LazyStaffApplication />,
    getParentRoute: () => fingerprintingStaffRoute,
  })

  const fingerprintingStaffApplicationManualCreateRoute = createRoute({
    path: 'application/manual/create',
    component: () => <LazyStaffManualApplicationCreate />,
    getParentRoute: () => fingerprintingStaffRoute,
  })

  const fingerprintingStaffApplicationDetailRoute = createRoute({
    path: 'application/$applicationId',
    component: () => <LazyStaffApplicationDetail />,
    getParentRoute: () => fingerprintingStaffRoute,
  })

  const fingerprintingStaffApplicationDetailDocumentRoute = createRoute({
    path: 'application/$applicationId/documents',
    component: () => <LazyStaffApplicationDetailDocument />,
    getParentRoute: () => fingerprintingStaffRoute,
  })

  const fingerprintingStaffApplicationDetailInternalDocumentsRoute = createRoute({
    path: 'application/$applicationId/internal-documents',
    component: () => <LazyStaffApplicationDetailInternalDocuments />,
    getParentRoute: () => fingerprintingStaffRoute,
  })

  const fingerprintingStaffDocumentRoute = createRoute({
    path: 'document',
    component: () => <LazyStaffDocumentPage />,
    getParentRoute: () => fingerprintingStaffRoute,
  })

  const fingerprintingStaffDocumentDetailRoute = createRoute({
    path: 'document/$applicationTypeCode',
    component: () => <LazyStaffApplicationTypeDetailPage />,
    getParentRoute: () => fingerprintingStaffRoute,
  })

  const fingerprintingStaffMessageTemplateRoute = createRoute({
    path: 'message-template',
    component: () => <LazyStaffMessageTemplatePage />,
    getParentRoute: () => fingerprintingStaffRoute,
  })

  const fingerprintingStaffChangePasswordRoute = createRoute({
    path: 'change-password',
    component: () => <LazyStaffChangePasswordPage />,
    getParentRoute: () => fingerprintingStaffRoute,
  })

  const fingerprintingStaffManagementRoute = createRoute({
    path: 'staff-management',
    component: () => <LazyStaffManagementPage />,
    getParentRoute: () => fingerprintingStaffRoute,
  })

  const fingerprintingStaffAuditLogRoute = createRoute({
    path: 'audit-log',
    component: () => <LazyStaffAuditLogPage />,
    getParentRoute: () => fingerprintingStaffRoute,
  })

  // Assemble the route tree
  const routeTree = fingerprintingRoute.addChildren([
    fingerprintingClientLoginRoute,
    fingerprintingConfirmDobRoute,
    fingerprintingForgotPasswordRoute,
    fingerprintingResetPasswordRoute,
    fingerprintingClientRoute.addChildren([
      fingerprintingClientApplicationRoute,
      fingerprintingClientApplicationDetailRoute,
      fingerprintingClientApplicationDetailDocumentRoute,
      fingerprintingClientApplicationDetailInstructionsRoute,
      fingerprintingClientInvoiceRoute,
      fingerprintingClientESignRoute,
      fingerprintingClientChangePasswordRoute,
    ]),
    fingerprintingStaffRoute.addChildren([
      fingerprintingStaffApplicationRoute,
      fingerprintingStaffApplicationManualCreateRoute,
      fingerprintingStaffApplicationDetailRoute,
      fingerprintingStaffApplicationDetailDocumentRoute,
      fingerprintingStaffApplicationDetailInternalDocumentsRoute,
      fingerprintingStaffDocumentRoute,
      fingerprintingStaffDocumentDetailRoute,
      fingerprintingStaffMessageTemplateRoute,
      fingerprintingStaffChangePasswordRoute,
      fingerprintingStaffManagementRoute,
      fingerprintingStaffAuditLogRoute,
    ]),
    fingerprintingClientChangePasswordVerifyRoute,
  ])

  return {
    fingerprintingRoute,
    routeTree,
  }
}
